import { AppContext, BookingTool, Organisation } from "../../../AppDataProvider/AppDataProvider";
import { Button as BaseButton, List, ListItem } from "../../../common/components";
import { marginBottom, marginRight, marginTop, media, paddingBottom, typography } from "../../../common/styles";
import React, { ReactElement, useContext } from "react";
import { applyOpacity } from "../../../common/styles/utils";
import CustomerTranslations from "../../../utils/CustomerTranslations";
import styled from "styled-components";
import { UnitOfMeasurement } from "../../../GeneratedGraphQL";
import { useTranslation } from "react-i18next";
export type OnSelectOrganisationOpts = {
  organisation: Organisation;
};
export type ListViewProps = {
  onSelectOrganisation: (options: OnSelectOrganisationOpts) => void;
  organisations: BookingTool["organisations"];
};
export function ListView(props: ListViewProps): ReactElement {
  const {
    t
  } = useTranslation();
  const {
    translateEntity
  } = CustomerTranslations.useCustomerTranslation();
  const {
    appData
  } = useContext(AppContext);
  const {
    organisations: organisationPage,
    onSelectOrganisation
  } = props;
  const {
    locationSelection
  } = appData.bookingTool;
  const searchAndMapEnabled = locationSelection ? locationSelection.searchAndMapEnabled : false;
  const unsortedOrganisations = organisationPage.edges.map(({
    node
  }) => node);
  const organisations = searchAndMapEnabled ? unsortedOrganisations : unsortedOrganisations.sort((a, b) => a.name.localeCompare(b.name));
  return <List data-sentry-element="List" data-sentry-component="ListView" data-sentry-source-file="ListView.tsx">
      {organisations.map((organisation, index) => {
      const translatedOrganisation = translateEntity({
        document: organisation,
        type: "account"
      });
      const name = searchAndMapEnabled ? `${index + 1}. ${translatedOrganisation.name}` : translatedOrganisation.name;
      const address = organisation.location?.address;
      const proximityToSearchedLocation = organisation.proximityToSearchedLocation;
      return <ListItem key={organisation._id}>
            <Content>
              <Name>{name}</Name>
              {organisation.location && <>
                  <Location>{address}</Location>
                  {proximityToSearchedLocation && <Distance>
                      <Label>{t("distance")}</Label>
                      <Value>
                        {`${proximityToSearchedLocation >= 10 ? proximityToSearchedLocation.toFixed(0) : proximityToSearchedLocation.toFixed(1)} ${appData.bookingTool.unitOfMeasurement === UnitOfMeasurement.Miles ? t("miles") : t("kilometres")}`}
                      </Value>
                    </Distance>}
                </>}
            </Content>
            <Button variant="primary" onClick={() => onSelectOrganisation({
          organisation
        })}>
              {t("select")}
            </Button>
          </ListItem>;
    })}
    </List>;
}
export default ListView;
const Content = styled.div`
  @media ${media.mobile} {
    ${paddingBottom.s}
  }
`;
const Name = styled.h2`
  ${typography.bodyBold}
  ${marginTop.zero}
  ${marginBottom.xs}
  /*
   * Was unset — renders {name} text directly inside common/components'
   * ListItem, which sets no ambient colour, so it silently fell back to
   * the browser default black.
   */
  color: ${({
  theme
}) => theme.brand.cssVars["--d-text"]};
`;
const Location = styled.div`
  ${typography.body}
  ${marginBottom.xs}
  /*
   * Was unset — same gap as Name above.
   */
  color: ${({
  theme
}) => theme.brand.cssVars["--d-muted"]};
`;
const Distance = styled.div`
  display: flex;
  flex-direction: row;
`;
const Label = styled.span`
  ${typography.small}
  color: ${({
  theme
}) => applyOpacity(theme.colours.dark, 0.6, false)};
  ${marginRight.s}
`;
const Value = styled.span`
  ${typography.small}
  /*
   * Was unset — renders the distance/unit text directly with no ambient
   * colour.
   */
  color: ${({
  theme
}) => theme.brand.cssVars["--d-text"]};
`;
const Button = styled(BaseButton)`
  min-width: 6.25rem;
`;